<script setup lang="ts">
import { ref, onMounted, watch, computed } from 'vue';
import useDashboardStore from '@/stores/Dashboard/dashboard-store';
import { DashboardWidget } from '@/interfaces/Dashboard';
import debounce from 'lodash/debounce';
import ConnxSkeletonExpiredLicencesCount from './ConnxSkeletonExpiredLicencesCount.vue';
import ConnxDashboardWidgetNoData from './ConnxDashboardWidgetNoData.vue';
import ConnxStatusCard from '@/components/Atoms/ConnxStatusCard.vue';
import type { Status } from '@/interfaces/Dashboard';
import ConnxStatusRowCard from '@/components/Atoms/ConnxStatusRowCard.vue';

const props = defineProps<{ height: number; type: string; widget: DashboardWidget }>();
const status = computed<Status>(() => {
  return 'danger';
});
const total = ref<number>(0);
const compliantPercent = ref<number>(0);
const compliantCount = ref<number>(0);
const toBeExpiredCount = ref<number>(0);
const noOfDaysBeforeExpiry = ref<number>(0);
const series = ref<any[] | null>(null);
const loading = ref(true);

const dashboardStore = useDashboardStore();

const statusSuccess = computed<Status>(() => 'success');
const statusWarning = computed<Status>(() => 'warning');

const seriesToShow = computed(() => {
  return (series.value && series.value.filter((s: any) => s.name !== '' && s.data !== 0)) ?? [];
});

const badgeText = computed(() => 'Compliant');

const labeltext = ref('');
const subtitletext = ref('');
const expirytext = ref('');

const showList = computed(() => {
  if (!props.widget) return false;
  if (props.widget.defaultJsonProperties.h <= 3 && props.widget.defaultJsonProperties.w <= 4) {
    return false;
  }
  return seriesToShow.value && seriesToShow.value.length > 0;
});

const fetchData = async () => {
  try {
    const data = await dashboardStore.fetchDataSource(props.widget.dataSourceGuid, {
      widgetGuid: props.widget.dashboardTabWidgetGuid,
    });

    if (data?.data) {
      total.value = data.data.total ?? 0;
      compliantPercent.value = data.data.compliantPercent ?? 0;
      compliantCount.value = data.data.compliantCount ?? 0;
      toBeExpiredCount.value = data.data.toBeExpiredCount ?? 0;
      noOfDaysBeforeExpiry.value = data.data.nofDaysBeforeExpiry ?? 0;
      series.value = data.data.series ?? [];

      labeltext.value = `${compliantCount.value} of ${total.value} employees`;
      subtitletext.value = `Expiring in ${noOfDaysBeforeExpiry.value} Days`;
      expirytext.value = `${toBeExpiredCount.value} employees`;
    }
  } catch (error) {
    console.error('Failed to fetch data:', error);
  } finally {
    loading.value = false;
  }
};

const debouncedFetchData = debounce(fetchData, 600);

watch(
  () => {
    const p = props.widget.defaultJsonProperties?.parameters;
    return {
      filterByValues: p?.filterBy?.map((f) => f.value) ?? [],
    };
  },
  async () => {
    await debouncedFetchData();
  },
  { deep: false }
);

onMounted(fetchData);
</script>
<template>
  <ConnxSkeletonExpiredLicencesCount v-if="loading" />

  <div v-else-if="series && series.length > 0" class="ScrollBar ScrollBar--vertical">
    <div
      :style="{ height: `${height}px` }"
      class="tw-w-full tw-h-full tw-pl-0 tw-pr-4 tw-pb-4 tw-pt-4 tw-flex tw-flex-col tw-gap-4"
    >
      <!-- Compliant Card -->
      <ConnxStatusCard
        :title="`${compliantPercent}%`"
        :status="statusSuccess"
        :badge-text="badgeText"
        :label-text="labeltext"
        :progressHeight="5"
        :ratio="{ value: compliantCount, total: total }"
      />

      <!-- Expiring in 30 Days Card -->
      <ConnxStatusCard
        :subtitle="subtitletext"
        :status="statusWarning"
        :badge-text="subtitletext"
        :label-text="expirytext"
        :progressHeight="5"
        :ratio="{ value: toBeExpiredCount, total: total }"
      />

      <!-- Immediate Action Section -->
      <div v-if="showList" class="tw-w-full tw-flex tw-flex-col tw-gap-2">
        <h4 class="tw-text-base tw-mb-2 tw-text-connx-grey-shade-55 tw-font-semibold">
          Number of non-compliant employees in position
        </h4>
        <div class="tw-flex tw-flex-col tw-gap-2 tw-w-full">
          <ConnxStatusRowCard
            v-for="(item, index) in seriesToShow"
            :key="index"
            :status="status"
            :title="item.name"
            :value="item.data"
          />
        </div>
      </div>
    </div>
  </div>

  <ConnxDashboardWidgetNoData v-else />
</template>
